/**
 * Weekly Agenda Day Row Component
 *
 * Renders one day (Mon-Sun) as a row in the weekly grid.
 * Contains a static tappable day label and animated cells per visible member.
 * Today's row gets a highlight.
 *
 * Follows single responsibility: renders a single day row.
 */

import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Animated, { useAnimatedStyle } from 'react-native-reanimated';
import type { SharedValue } from 'react-native-reanimated';
import { Colors, Shadows } from '@/constants/theme';
import { BorderRadius } from '@/constants/spacing';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { WeeklyAgendaCell } from './WeeklyAgendaCell';
import type { WeeklyAgendaDayRowData } from '@/types/weekly-agenda';
import type { FamilyMemberResource } from '@/types/family-resource';
import type { CalendarEvent } from '@/types/events';

const DAY_LABEL_WIDTH = 65;

type WeeklyAgendaDayRowProps = {
  dayRow: WeeklyAgendaDayRowData;
  visibleResources: FamilyMemberResource[];
  columnWidth: number;
  onDayPress: (dateKey: string) => void;
  onEventPress?: (event: CalendarEvent) => void;
  onCreateEvent?: (dateKey: string, memberId: string) => void;
  translateX: SharedValue<number>;
  selectedDateKey?: string;
};

export const WeeklyAgendaDayRow = React.memo(function WeeklyAgendaDayRow({
  dayRow,
  visibleResources,
  columnWidth,
  onDayPress,
  onEventPress,
  onCreateEvent,
  translateX,
  selectedDateKey,
}: WeeklyAgendaDayRowProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  const isSelected = !dayRow.isToday && dayRow.dateKey === selectedDateKey;
  const rowBackground = dayRow.isToday
    ? palette.todayHighlight
    : isSelected
      ? palette.selectedDayHighlight
      : 'transparent';
  const labelWrapperBackground = dayRow.isToday
    ? palette.todayHighlight
    : isSelected
      ? palette.selectedDayHighlight
      : palette.background;
  const labelBorderColor = dayRow.isToday
    ? palette.primary
    : isSelected
      ? palette.secondary
      : palette.border;
  const labelBackground = dayRow.isToday
    ? palette.todayHighlight
    : isSelected
      ? palette.selectedDayHighlight
      : palette.cardBackground;

  const memberColorMap = new Map(
    visibleResources.map(r => [r.id, r.color])
  );

  const visibleCells = dayRow.cells.filter(cell =>
    visibleResources.some(r => r.id === cell.memberId)
  );

  const cellsAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: translateX.value }],
  }));

  const labelColor = dayRow.isToday ? palette.primary : palette.text;

  return (
    <View
      style={[
        styles.row,
        {
          borderBottomColor: palette.border,
          backgroundColor: rowBackground,
        },
      ]}
    >
      {/* Static day label column — elevated so cells swipe behind it */}
      <View
        style={[
          styles.dayLabelWrapper,
          {
            backgroundColor: labelWrapperBackground,
            borderRightColor: palette.border,
          },
        ]}
      >
        <TouchableOpacity
          style={[
            styles.dayLabelContainer,
            {
              borderWidth: 1.5,
              borderColor: labelBorderColor,
              borderRadius: BorderRadius.sm,
              backgroundColor: labelBackground,
            },
            Shadows.mdBottomRight,
          ]}
          onPress={() => onDayPress(dayRow.dateKey)}
          activeOpacity={0.5}
        >
          <Text style={[styles.dayAbbrev, { color: labelColor }]}>
            {dayRow.dayLabel.dayAbbrev}
          </Text>
          <Text
            style={[
              styles.dateNum,
              { color: labelColor },
              dayRow.isToday && styles.dateNumToday,
            ]}
          >
            {dayRow.dayLabel.dateNum}
          </Text>
          <Text style={[styles.monthAbbrev, { color: labelColor }]}>
            {dayRow.dayLabel.monthAbbrev}
          </Text>
        </TouchableOpacity>
      </View>

      {/* Animated member cells */}
      <Animated.View style={[styles.cellsContainer, cellsAnimatedStyle]}>
        {visibleCells.map(cell => (
          <WeeklyAgendaCell
            key={cell.memberId}
            cellData={cell}
            dateKey={dayRow.dateKey}
            memberColor={memberColorMap.get(cell.memberId) ?? '#4CAF50'}
            columnWidth={columnWidth}
            onEventPress={onEventPress}
            onCreateEvent={onCreateEvent}
          />
        ))}
      </Animated.View>
    </View>
  );
});

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    borderBottomWidth: 2,
    alignItems: 'flex-start',
  },
  dayLabelWrapper: {
    width: DAY_LABEL_WIDTH,
    zIndex: 1,
    borderRightWidth: 1,
    alignSelf: 'stretch',
  },
  dayLabelContainer: {
    paddingVertical: 8,
    paddingHorizontal: 4,
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginVertical: 4,
    marginHorizontal: 4,
  },
  dayAbbrev: {
    fontSize: 9,
    fontWeight: '500',
    textAlign: 'center',
    letterSpacing: 0.5,
  },
  dateNum: {
    fontSize: 18,
    fontWeight: '500',
    textAlign: 'center',
    lineHeight: 22,
  },
  dateNumToday: {
    fontWeight: '600',
  },
  monthAbbrev: {
    fontSize: 9,
    fontWeight: '500',
    textAlign: 'center',
    letterSpacing: 0.5,
  },
  cellsContainer: {
    flexDirection: 'row',
    flex: 1,
  },
});
